import React, { useState, useRef, useEffect } from 'react';
import {
  AppBar, Toolbar, Button, IconButton, Badge,
  BottomNavigation, BottomNavigationAction, Box, Divider, List,
  ListItem, ListItemText, ListItemIcon, CircularProgress,
  Paper, ClickAwayListener, Popper, Typography, Switch,
  FormControlLabel, Snackbar, Alert
} from '@mui/material';
import {
  ShoppingCart, AccountCircle, Home, Wallet,
  AdminPanelSettings, Notifications as NotificationsIcon,
  NotificationsNone, DoneAll, Close, Logout,VolumeUp,VolumeOff,HeadsetMic
} from '@mui/icons-material';
import PhoneIcon from '@mui/icons-material/Phone';
import ShareIcon from '@mui/icons-material/Share';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNotifications } from '../context/NotificationContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '@mui/material/styles';

const NavBar = ({keywordsData , language, toggleLanguage}) => {
  const { cartCount, setCartOpen } = useCart();
  // Inside your component:
const theme = useTheme();
const isRtl = theme.direction === 'rtl';
  const { user, logout } = useAuth();
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    loading,
    soundEnabled,
    toggleSound
  } = useNotifications();

  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [pharmacyStatus, setPharmacyStatus] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tableScan, setTableScan] = useState(Number(localStorage.getItem('tableId')) || 0);
  const notificationRef = useRef(null);

  const getCurrentNavigationValue = () => {
    const path = location.pathname;
    if (path === '/' || path === '/home') return 'home';
    if (path === '/account') return 'account';
    if (path === '/wallet') return 'wallet';
    if (path === '/admin') return 'admin';
    if (path === '/super') return 'super';
    if (path === '/about') return 'about';    
    if (path === '/admin/chats') return 'admin/chats';    
    if (path === '/super/chats') return 'super/chats';    
    return '';
  };

  const [value, setValue] = useState(getCurrentNavigationValue());

  useEffect(() => {
    setValue(getCurrentNavigationValue());
  }, [location.pathname]);

  useEffect(() => {
    const fetchPharmacyStatus = async () => {
      try {
        const response = await api.get('/categories/pharm_status');
        setPharmacyStatus(response.data.value === '1');
      } catch (error) {
        console.error('Error fetching pharmacy status:', error);
        const savedStatus = localStorage.getItem('pharmacyStatus');
        if (savedStatus !== null) {
          setPharmacyStatus(savedStatus === 'true');
        }
      }
    };
    if(user?.role === 'admin') fetchPharmacyStatus();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNotificationClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openStatusTxt = language === 'ar' ?  keywordsData.open_caffe_status_txt_ar : keywordsData.open_caffe_status_txt_en ;
  const closedStatusTxt = language === 'ar' ?  keywordsData.closed_caffe_status_txt_ar : keywordsData.closed_caffe_status_txt_en ;
  const openTxt = language === 'ar' ?  keywordsData.open_status_txt_ar : keywordsData.open_status_txt_en ;
  const closedTxt = language === 'ar' ?  keywordsData.closed_status_txt_ar : keywordsData.closed_status_txt_en ;

  const handleStatusChange = async (event) => {
    const newStatus = event.target.checked;
    setLoadingStatus(true);
    
    try {
      const response = await api.put('/admin/pharm_status', {
        key: 'pharm_open_status',
        value: newStatus ? '1' : '0'
      });

      setPharmacyStatus(newStatus);
      localStorage.setItem('pharmacyStatus', newStatus);
      
      setSnackbar({
        open: true,
        message: newStatus ? openStatusTxt : closedStatusTxt,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating pharmacy status:', error);
      setSnackbar({
        open: true,
        message: 'فشل في تحديث حالة المتجر',
        severity: 'error'
      });
      setPharmacyStatus(!newStatus);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const [isCopied, setIsCopied] = useState(false);
  const currentUrl = window.location.href;
  const siteTitle = document.title;

  const shareWithWebAPI = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: siteTitle,
          text: `تابع أحدث العروض على ${siteTitle}`,
          url: currentUrl,
        });
        console.log('Shared successfully');
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      console.log('Web Share API not supported');
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentUrl).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popper' : undefined;

  return (
    <>
      {/* App Bar */}
      <AppBar
        position="sticky"
        sx={{
          top: 0,
          background: language === 'ar' ? 'linear-gradient(120deg,#36342af6 90%, #000000f4 100%)' : 'linear-gradient(120deg, #000000f4 0%, #36342af6 100%)',
          borderBottom: '2px solid #ffbb00', // Yellow border bottom
          boxShadow: '0 4px 12px rgba(255, 215, 0, 0.15)',
          padding: '8px 0',
          zIndex: 1201
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          
          {/* Reto Cafe Circular Logo - Black & Yellow Version */}
          <Box
  sx={{
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    height: "70px",
    maxHeight: "70px",
    width: "100px",
    "&:hover": {
      transform: "scale(1.05) rotate(2deg)",
      transition: "transform 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
      filter: "drop-shadow(0 0 12px rgba(255, 215, 0, 0.8))"
    }
  }}
  onClick={() => navigate("/")}
>
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 120 120"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="mainGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFD700" />
        <stop offset="50%" stopColor="#FFA500" />
        <stop offset="100%" stopColor="#FF6347" />
      </linearGradient>
      
      <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFD700" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#FF4500" stopOpacity="0" />
      </radialGradient>
      
      <filter id="glow">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>

    {/* 3D outer ring */}
    <circle
      cx="60"
      cy="60"
      r="56"
      fill="none"
      stroke="url(#mainGradient)"
      strokeWidth="3"
      filter="url(#glow)"
    />
    <circle
      cx="60"
      cy="60"
      r="53"
      fill="none"
      stroke="rgba(255, 215, 0, 0.3)"
      strokeWidth="2"
    />
    
    {/* Inner background with gradient */}
    <circle cx="60" cy="60" r="47" fill="url(#centerGlow)" />
    <circle cx="60" cy="60" r="44" fill="#0F0F0F" />
    
    {/* Currency/Coin Icon */}
    <g transform="translate(52, 40)">
      {/* Coin circle */}
      <circle cx="8" cy="1" r="12" fill="none" stroke="#FFD700" strokeWidth="1.2" />
      {/* Dollar sign */}
      <text x="8" y="5" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#FFD700" fontFamily="monospace">$</text>
    </g>
    
    {/* JOMLA text */}
    <text
      x="60"
      y="72"
      textAnchor="middle"
      fontSize="18"
      fontWeight="800"
      fill="#FFD700"
      fontFamily="'Montserrat', 'Poppins', sans-serif"
      letterSpacing="1.5"
      stroke="#FF4500"
      strokeWidth="0.5"
      paintOrder="stroke"
    >
      JOMLA
    </text>
    
    {/* LINE text */}
    <text
      x="60"
      y="92"
      textAnchor="middle"
      fontSize="15"
      fontWeight="600"
      fill="#FFA500"
      fontFamily="'Montserrat', sans-serif"
      letterSpacing="1.8"
      stroke="#D32F2F"
      strokeWidth="0.3"
      paintOrder="stroke"
    >
      LINE
    </text>
    
    {/* Accent lines */}
    <line x1="35" y1="78" x2="45" y2="78" stroke="#FFD700" strokeWidth="1" strokeOpacity="0.5" />
    <line x1="75" y1="78" x2="85" y2="78" stroke="#FFD700" strokeWidth="1" strokeOpacity="0.5" />
  </svg>
</Box>

          {/* Right Side Buttons - Black & Yellow Theme */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {/* Cart Button */}
                <IconButton 
                  onClick={() => setCartOpen(true)}
                  sx={{
                    color: '#FFFFFF',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    }
                  }}
                >
                  <Badge 
                    badgeContent={cartCount} 
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        fontWeight: 'bold',
                      }
                    }}
                  >
                    <ShoppingCart />
                  </Badge>
                </IconButton>
            {user ? (
              <>
                {/* Pharmacy Status Toggle (Admin Only) - Custom Yellow Switch */}
                {user.role === 'admin' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    {loadingStatus ? (
                      <CircularProgress size={24} sx={{ color: '#FFD700' }} />
                    ) : (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={pharmacyStatus}
                            onChange={handleStatusChange}
                            disabled={loadingStatus}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#FFD700',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 215, 0, 0.08)',
                                },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#FFD700',
                              },
                              '& .MuiSwitch-track': {
                                backgroundColor: '#2D2D2D',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                            {pharmacyStatus ? openTxt : closedTxt}
                          </Typography>
                        }
                        labelPlacement="start"
                      />
                    )}
                  </Box>
                )}
                
                {/* Notifications Button */}
                <IconButton
                  ref={notificationRef}
                  onClick={handleNotificationClick}
                  sx={{
                    color: '#FFFFFF',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    }
                  }}
                >
                  <Badge 
                    badgeContent={unreadCount}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#FFD700',
                        color: '#000000',
                        fontWeight: 'bold',
                      }
                    }}
                  >
                    {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNone />}
                  </Badge>
                </IconButton>
                
                {/* Logout Button */}
                <Button
                  onClick={handleLogout}
                  sx={{
                    color: '#FFFFFF',
                    minWidth: 'auto',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    }
                  }}
                >
                  <Logout sx={{ fontSize: '1.5rem' }} />
                </Button>
                
                {/* Share Button */}
              <Button 
                onClick={toggleLanguage}
                sx={{
                    color: '#FFFFFF',
                    minWidth: 'auto',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    }
                }}>
                {language === 'ar' ? 'EN' : 'AR'}
              </Button>
              </>
            ) : (
              <>
                <Button 
                  color="inherit" 
                  component={Link} 
                  to="/login"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 600,
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    }
                  }}
                >
                  {language === 'ar' ? keywordsData.login_ar : keywordsData.login_en}
                </Button>
                <Button 
                  color="inherit" 
                  component={Link} 
                  to="/register"
                  sx={{
                    color: '#FFFFFF',
                    fontWeight: 600,
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    }
                  }}
                > 
                 {language === 'ar' ? keywordsData.register_ar : keywordsData.register_en}
                </Button>
              <Button 
                onClick={toggleLanguage}
                sx={{
                    color: '#FFFFFF',
                    minWidth: 'auto',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    }
                }}>
                {language === 'ar' ? 'EN' : 'AR'}
              </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Notifications Dropdown - Black & Yellow Theme */}
      <Popper
        id={id}
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              width: 380,
              maxHeight: 450,
              overflow: 'auto',
              boxShadow: '0 8px 24px rgba(255, 215, 0, 0.15)',
              backgroundColor: '#0D0D0D',
              border: '1px solid #FFD700',
              borderRadius: '12px',
              mt: 1
            }}
          >
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: '1px solid #2D2D2D'
            }}>
              <Typography variant="h6" sx={{ color: '#FFD700', fontWeight: 700 }}>
                {language === 'ar' ? keywordsData.noty_title_ar : keywordsData.noty_title_en}
              </Typography>
              <Box>
                <IconButton 
                  size="small" 
                  onClick={markAllAsRead} 
                  disabled={unreadCount === 0}
                  sx={{
                    color: '#B3B3B3',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    },
                    '&.Mui-disabled': {
                      color: '#2D2D2D',
                    }
                  }}
                >
                  <DoneAll fontSize="small" />
                </IconButton>
                <IconButton onClick={toggleSound} sx={{ color: soundEnabled ? '#FFD700' : '#B3B3B3' }}>
                  {soundEnabled ? <VolumeUp /> : <VolumeOff />}
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={handleClose}
                  sx={{
                    color: '#B3B3B3',
                    '&:hover': {
                      color: '#FFD700',
                      backgroundColor: 'rgba(255, 215, 0, 0.08)',
                    }
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} sx={{ color: '#FFD700' }} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography sx={{ color: '#B3B3B3' }}>{ language === 'ar' ? keywordsData.noty_no_record_ar : keywordsData.noty_no_record_en }</Typography>
              </Box>
            ) : (
              <List dense>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    sx={{
                      backgroundColor: notification.read ? 'transparent' : 'rgba(255, 215, 0, 0.08)',
                      cursor: 'pointer',
                      borderBottom: '1px solid #2D2D2D',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 215, 0, 0.15)',
                      },
                      py: 1.5
                    }}
                    onClick={() => {
                      markAsRead(notification.id);
                      if (notification.link) {
                        navigate(notification.link);
                      }
                      handleClose();
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {notification.icon === 'order' && <ShoppingCart fontSize="small" sx={{ color: '#FFD700' }} />}
                      {notification.icon === 'system' && <AdminPanelSettings fontSize="small" sx={{ color: '#FFD700' }} />}
                    </ListItemIcon>
                    <ListItemText
                      primary={notification.title}
                      secondary={notification.message}
                      primaryTypographyProps={{
                        fontWeight: notification.read ? 'normal' : 'bold',
                        color: notification.read ? '#B3B3B3' : '#FFFFFF',
                        fontSize: '0.9rem'
                      }}
                      secondaryTypographyProps={{
                        color: '#6B6B6B',
                        fontSize: '0.8rem'
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#6B6B6B', minWidth: 60, textAlign: 'left' }}>
                      {new Date(notification.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Bottom Navigation - Black & Yellow Theme */}
      <BottomNavigation
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue);
          navigate(`/${newValue === 'home' ? '' : newValue}`);
        }}
        showLabels
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          backgroundColor: '#000000', // Pure black
          borderTop: '2px solid #FFD700', // Yellow border top
          height: 70,
          paddingBottom: '8px',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.5)'
        }}
      >
        <BottomNavigationAction 
          label={ language === 'ar' ? keywordsData.home_txt_ar : keywordsData.home_txt_en }
          value="home" 
          icon={<Home />} 
          sx={{
            color: '#6B6B6B',
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              color: '#6B6B6B',
              fontWeight: 500,
            },
            '& .MuiSvgIcon-root': {
              color: '#6B6B6B',
              fontSize: '1.6rem',
            },
            '&.Mui-selected': {
              color: '#FFD700',
              '& .MuiBottomNavigationAction-label': {
                color: '#FFD700',
                fontSize: '0.75rem',
                fontWeight: 800,
              },
              '& .MuiSvgIcon-root': {
                color: '#FFD700',
              },
            },
          }} 
        />
        <BottomNavigationAction 
          label={ language === 'ar' ? keywordsData.account_txt_ar : keywordsData.account_txt_en }
          value="account" 
          icon={<AccountCircle />} 
          sx={{
            color: '#6B6B6B',
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              color: '#6B6B6B',
            },
            '& .MuiSvgIcon-root': {
              color: '#6B6B6B',
              fontSize: '1.6rem',
            },
            '&.Mui-selected': {
              color: '#FFD700',
              '& .MuiBottomNavigationAction-label': {
                color: '#FFD700',
                fontSize: '0.75rem',
                fontWeight: 800,
              },
              '& .MuiSvgIcon-root': {
                color: '#FFD700',
              },
            },
          }} 
        />
        <BottomNavigationAction 
          label={ language === 'ar' ? keywordsData.wallet_txt_ar : keywordsData.wallet_txt_en }
          value="wallet" 
          icon={<Wallet />} 
          sx={{
            color: '#6B6B6B',
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              color: '#6B6B6B',
            },
            '& .MuiSvgIcon-root': {
              color: '#6B6B6B',
              fontSize: '1.6rem',
            },
            '&.Mui-selected': {
              color: '#FFD700',
              '& .MuiBottomNavigationAction-label': {
                color: '#FFD700',
                fontSize: '0.75rem',
                fontWeight: 800,
              },
              '& .MuiSvgIcon-root': {
                color: '#FFD700',
              },
            },
          }} 
        />
{['admin', 'super'].includes(user?.role) && (
  <BottomNavigationAction 
    label={language === 'ar' ? 'المحادثات' : 'Chats'}
    value={user?.role === 'admin' ? "admin/chats" : "super/chats"}
    icon={<HeadsetMic />} 
    sx={{
      color: '#6B6B6B',
      '& .MuiBottomNavigationAction-label': {
        fontSize: '0.7rem',
        color: '#6B6B6B',
      },
      '& .MuiSvgIcon-root': {
        color: '#6B6B6B',
        fontSize: '1.6rem',
      },
      '&.Mui-selected': {
        color: '#FFD700',
        '& .MuiBottomNavigationAction-label': {
          color: '#FFD700',
          fontSize: '0.75rem',
          fontWeight: 800,
        },
        '& .MuiSvgIcon-root': {
          color: '#FFD700',
        },
      },
    }} 
  />
)}
        {user?.role === 'admin' ? (
          <BottomNavigationAction 
            label={ language === 'ar' ? keywordsData.dashboard_txt_ar : keywordsData.dashboard_txt_en }
            value="admin" 
            icon={<AdminPanelSettings />} 
            sx={{
              color: '#6B6B6B',
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                color: '#6B6B6B',
              },
              '& .MuiSvgIcon-root': {
                color: '#6B6B6B',
                fontSize: '1.6rem',
              },
              '&.Mui-selected': {
                color: '#FFD700',
                '& .MuiBottomNavigationAction-label': {
                  color: '#FFD700',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                },
                '& .MuiSvgIcon-root': {
                  color: '#FFD700',
                },
              },
            }} 
          />
        ) : user?.role === 'super' ? (
          <BottomNavigationAction 
            label={ language === 'ar' ? keywordsData.dashboard_txt_ar : keywordsData.dashboard_txt_en }
            value="super" 
            icon={<AdminPanelSettings />} 
            sx={{
              color: '#6B6B6B',
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                color: '#6B6B6B',
              },
              '& .MuiSvgIcon-root': {
                color: '#6B6B6B',
                fontSize: '1.6rem',
              },
              '&.Mui-selected': {
                color: '#FFD700',
                '& .MuiBottomNavigationAction-label': {
                  color: '#FFD700',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                },
                '& .MuiSvgIcon-root': {
                  color: '#FFD700',
                },
              },
            }} 
          />
        ) : (
          <BottomNavigationAction 
            label={ language === 'ar' ? keywordsData.contact_txt_ar : keywordsData.contact_txt_en }
            value="about" 
            icon={<PhoneIcon />} 
            sx={{
              color: '#6B6B6B',
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.7rem',
                color: '#6B6B6B',
              },
              '& .MuiSvgIcon-root': {
                color: '#6B6B6B',
                fontSize: '1.6rem',
              },
              '&.Mui-selected': {
                color: '#FFD700',
                '& .MuiBottomNavigationAction-label': {
                  color: '#FFD700',
                  fontSize: '0.75rem',
                  fontWeight: 800,
                },
                '& .MuiSvgIcon-root': {
                  color: '#FFD700',
                },
              },
            }} 
          />
        )}
      </BottomNavigation>

      {/* Snackbar for status updates - Black & Yellow Theme */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            border: '1px solid #FFD700',
            borderRadius: '12px',
            fontWeight: 600,
            '& .MuiAlert-icon': {
              color: snackbar.severity === 'success' ? '#FFD700' : '#FF3D00',
            },
            '& .MuiAlert-action': {
              color: '#B3B3B3',
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NavBar;